import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';

interface AuthContextType {
  isAuthenticated: boolean;
  userEmail: string | null;
  pendingEmail: string | null;
  isLoading: boolean;
  login: (email: string) => Promise<{ success: boolean; error?: string }>;
  verifyOTP: (otp: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  setPendingEmail: (email: string) => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const MAX_AUTH_ERROR_SNIPPET = 300;

const truncateForLog = (value: string, limit = MAX_AUTH_ERROR_SNIPPET): string => {
  if (!value) return '';
  if (value.length <= limit) return value;
  return `${value.slice(0, limit)}...`;
};

const normalizeDetail = (detail: unknown): string => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((item) => normalizeDetail(item)).join('; ');
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return '';
};

const readErrorMessage = async (response: Response, context: string): Promise<string> => {
  const contentType = response.headers.get('content-type') || '';
  const requestId = response.headers.get('x-request-id') || '-';
  const bodyText = await response.text();
  const snippet = truncateForLog(bodyText.replace(/\s+/g, ' ').trim());

  let detail = '';
  if (bodyText) {
    try {
      const parsed = JSON.parse(bodyText) as { detail?: unknown };
      detail = normalizeDetail(parsed?.detail);
    } catch {
      detail = '';
    }
  }

  console.error(`[API][${context}] request failed`, {
    status: response.status,
    statusText: response.statusText,
    contentType,
    requestId,
    bodySnippet: snippet,
  });

  const message = detail || snippet || `${response.status} ${response.statusText}`;
  return `${message} (status ${response.status}, request-id ${requestId})`;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const [pendingEmail, setPendingEmailState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasCheckedAuth, setHasCheckedAuth] = useState<boolean>(false);

  // Check if user is authenticated on mount (via httpOnly cookie)
  const checkAuth = useCallback(async () => {
    // Prevent multiple auth checks
    if (hasCheckedAuth) return;
    setHasCheckedAuth(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        credentials: 'include', // Include cookies
      });

      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setUserEmail(data.data.email);
      } else {
        if (response.status >= 500) {
          const errorMessage = await readErrorMessage(response, 'auth/me');
          console.error('[API][auth/me] non-success response', { errorMessage });
        }
        setIsAuthenticated(false);
        setUserEmail(null);
      }
    } catch {
      setIsAuthenticated(false);
      setUserEmail(null);
    } finally {
      setIsLoading(false);
    }
  }, [hasCheckedAuth]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
        credentials: 'include',
      });

      if (response.ok) {
        setPendingEmailState(email);
        return { success: true };
      } else {
        const errorMessage = await readErrorMessage(response, 'auth/login');
        return { success: false, error: errorMessage || 'Failed to send OTP' };
      }
    } catch {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const verifyOTP = async (otp: string): Promise<{ success: boolean; error?: string }> => {
    if (!pendingEmail) {
      return { success: false, error: 'No email pending verification' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: pendingEmail, code: otp }),
        credentials: 'include', // Required to receive and store httpOnly cookie
      });

      if (response.ok) {
        setIsAuthenticated(true);
        setUserEmail(pendingEmail);
        setPendingEmailState(null);
        return { success: true };
      } else {
        const errorMessage = await readErrorMessage(response, 'auth/verify');
        return { success: false, error: errorMessage || 'Invalid OTP' };
      }
    } catch (e){
        console.log(e)
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      setIsAuthenticated(false);
      setUserEmail(null);
      setPendingEmailState(null);
    }
  };

  const setPendingEmail = (email: string) => {
    setPendingEmailState(email);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userEmail,
        pendingEmail,
        isLoading,
        login,
        verifyOTP,
        logout,
        setPendingEmail,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
